import {
  TideClientConfig,
  confirm,
  createCrmContact,
  CrmContactRequest,
  forgotPassword,
  getCountries,
  getCustomEntryStatus,
  getEntryList,
  getEntryStatus,
  getInvoiceList,
  login,
  logout,
  print,
  resetPassword,
  AgentInvoiceItem,
  AgentPrintActionRequest,
  Column,
  CustomEntryStatusItem,
  DossierViewResult,
  FilterItem,
  MemberConfirmationRequest,
  MemberForgotPasswordRequest,
  MemberLoginRequest,
  MemberLoginResponse,
  MemberResetPasswordRequest,
  NumberStringPair,
  PageResult,
  ContactFormRequest,
  sendContactForm,
} from "@qite/tide-client";
export const officeId = 1;
export const webAgentId = 10301;
export const customWebOfferStatusId = 6;

const HOST = process.env.TIDE_HOST;
const API_KEY = process.env.API_KEY;
const PORTAL_ID = parseInt(process.env.PORTAL_ID || "0");

interface TideContactRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface Traveler {
  id: number;
  guid: string;
  age?: number;
  firstName?: string;
  lastName?: string;
}

interface CustomerRequest {
  id: number;
  title: string;
  message: string;
}

interface Room {
  accommodationCode?: string;
  regimeCode?: string;
  travelers: Traveler[];
  customerRequests?: CustomerRequest[];
}

interface TideOfferRequest {
  officeId: number;
  agentId?: number;
  customEntryStatusId?: number;
  productCode?: string;
  startDate?: string;
  endDate?: string;
  address: {
    email: string;
    mobilePhone: string;
    street?: string;
    country?: string;
    location?: string;
  };
  rooms: Room[];
}

// createCrmContact and sendContactForm resolve the parsed response body (null for an empty
// body), not the fetch Response their typings claim, and the tide-client already throws on a
// non-ok response. Checking `response.ok` here therefore threw on a perfectly successful
// call, which is why a submit that returned 200 still reported a failure to the visitor.
export async function requestCrmcontact(request: CrmContactRequest): Promise<void> {
  const HOST = process.env.TIDE_HOST;
  const API_KEY = process.env.API_KEY;
  const tideClientConfig: TideClientConfig = { host: HOST!, apiKey: API_KEY! };
  await createCrmContact(tideClientConfig, request, undefined);
}

export async function requestContact(request: ContactFormRequest): Promise<void> {
  const HOST = process.env.TIDE_HOST;
  const API_KEY = process.env.API_KEY;
  const tideClientConfig: TideClientConfig = { host: HOST!, apiKey: API_KEY! };
  await sendContactForm(tideClientConfig, request, undefined);
}

export const confirmMember = async (token: string, password: string, hasAgreed: boolean) => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
  };

  const memberConfirmationRequest: MemberConfirmationRequest = {
    token: token,
    password: password,
    hasAgreed: hasAgreed,
  };

  const response = await confirm(tideClientConfig, memberConfirmationRequest, undefined);
  return response;
};

export const loginMember = async (
  username: string,
  password: string
): Promise<MemberLoginResponse> => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
  };

  const memberLogingRequest: MemberLoginRequest = {
    username: username,
    password: password,
    portalId: PORTAL_ID,
  };

  const memberLoginResponse = await login(tideClientConfig, memberLogingRequest, undefined);
  return memberLoginResponse;
};

export const logoutMember = async () => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
  };

  const response = await logout(tideClientConfig, undefined);
  return response.ok;
};

export const passwordForgotten = async (username: string) => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
  };

  const memberForgotPasswordRequest: MemberForgotPasswordRequest = {
    username: username,
    portalId: PORTAL_ID,
  };

  const response = await forgotPassword(tideClientConfig, memberForgotPasswordRequest, undefined);
  return response.ok;
};

export const newPassword = async (token: string, password: string) => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
  };

  const memberResetPasswordRequest: MemberResetPasswordRequest = {
    token: token,
    password: password,
  };

  const response = await resetPassword(tideClientConfig, memberResetPasswordRequest, undefined);
  return response.ok;
};

export const getDossier = async (
  filterItem: FilterItem,
  token: string,
  signal: AbortSignal,
  gridColumns?: Column[] | undefined
): Promise<PageResult<DossierViewResult>> => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
    token: token,
  };

  const response = await getEntryList(tideClientConfig, filterItem, gridColumns, signal);
  return response;
};

export const getInvoices = async (
  filterItem: FilterItem,
  token: string,
  signal: AbortSignal,
  gridColumns?: Column[] | undefined
): Promise<PageResult<AgentInvoiceItem>> => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
    token: token,
  };

  const response = await getInvoiceList(tideClientConfig, filterItem, gridColumns, signal);
  return response;
};

export const getEntryStatusList = async (languageCode: string): Promise<NumberStringPair[]> => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig = buildTideClientConfig();
  const response = await getEntryStatus(tideClientConfig, undefined, languageCode);
  return response;
};

export const getCustomEntryStatusList = async (
  languageCode: string
): Promise<CustomEntryStatusItem[]> => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig = buildTideClientConfig();
  const response = await getCustomEntryStatus(tideClientConfig, undefined, languageCode);
  return response;
};

export const getCountryList = async (signal?: AbortSignal) => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
  };

  const response = await getCountries(tideClientConfig, signal);
  return response;
};

export const printAction = async (
  request: AgentPrintActionRequest,
  token: string,
  signal?: AbortSignal
): Promise<Blob> => {
  if (!HOST || !API_KEY) {
    return Promise.reject();
  }

  const tideClientConfig: TideClientConfig = {
    host: HOST,
    apiKey: API_KEY,
    token: token,
  };

  const response = await print(tideClientConfig, request, signal);

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  const body = await response.blob();

  return body;
};

export const buildTideClientConfig = (token?: string): TideClientConfig => {
  return <TideClientConfig>{
    host: HOST,
    apiKey: API_KEY,
    token: token,
  };
};
